/**
 * SessionHomePage — agent-led beta landing (allowlisted learners only).
 *
 * Greeting + coach chat with session-home intent routing
 * (CONTINUE / REVIEW / NEW). Curriculum tree remains available via Browse.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LearnerNav from '../components/nav/LearnerNav';
import ChatWidget from '../components/ChatWidget';

const QUICK_ACTIONS = [
  { label: 'Continue', message: 'I want to continue where I left off.' },
  { label: 'Quick review', message: 'I want a quick review of what I have finished.' },
  { label: 'Something new', message: 'I want to start something new from topics I can cover.' },
  { label: 'My progress', message: 'How am I doing — what is left on my track?' },
];

export default function SessionHomePage() {
  const { learner, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const enrolled = learner?.enrolled_tracks || learner?.enrolled_domains || [];
    if (Array.isArray(enrolled)) {
      setTracks(enrolled.map((t) => (typeof t === 'string' ? t : t.track_id || t.domain_id || t)));
    } else if (enrolled && typeof enrolled === 'object') {
      const ai = enrolled.ai || [];
      setTracks(Array.isArray(ai) ? ai : Object.keys(enrolled));
    }
  }, [learner]);

  const lessonContext = useMemo(
    () => ({
      surface: 'session_home',
      learner_id: learner?.learner_id,
      display_name: learner?.display_name || learner?.email,
      enrolled_tracks: learner?.enrolled_tracks || tracks,
      has_progress: Boolean(tracks.length),
    }),
    [learner, tracks],
  );

  const name = learner?.display_name || learner?.email || 'there';

  const handleLaunch = (launch) => {
    if (!launch) return;
    if (launch.path) {
      navigate(launch.path);
      return;
    }
    const domain = launch.params?.domain || launch.domain || 'ai';
    if (launch.type === 'flashcard_review' || launch.surface_id === 'flashcard_review') {
      navigate(`/flashcards/${domain}`);
      return;
    }
    if (launch.surface_id === 'domain_hub' || launch.type === 'domain') {
      navigate(`/domain/${domain}`);
      return;
    }
    if (launch.surface_id === 'learner_dashboard' || launch.type === 'dashboard') {
      navigate('/dashboard');
      return;
    }
    if (launch.surface_id === 'session_home') {
      navigate('/session-home');
      return;
    }
    navigate(`/domain/${domain}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' }}>
      <LearnerNav />
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 120px' }}>
        <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 8px', letterSpacing: 0.4 }}>
          BETA · AI domain · Personal coach session
        </p>
        <h1 style={{ margin: '0 0 12px', fontSize: 28, fontWeight: 700 }}>
          Hi {name} — ready when you are
        </h1>
        <p style={{ color: '#cbd5e1', lineHeight: 1.6, marginBottom: 28 }}>
          Open the coach chat to continue where you left off, do a quick review
          with released flashcards, or start an unlocked topic. Your full
          curriculum is still one click away.
        </p>

        {tracks.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>ENROLLED</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {tracks.map((t) => (
                <span
                  key={t}
                  style={{
                    background: 'rgba(99,102,241,0.15)',
                    border: '1px solid rgba(99,102,241,0.35)',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                  }}
                >
                  {String(t).replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Link
            to="/dashboard"
            style={{
              display: 'block',
              textAlign: 'center',
              background: 'transparent',
              color: '#cbd5e1',
              textDecoration: 'none',
              borderRadius: 10,
              padding: '12px 18px',
              fontWeight: 500,
              border: '1px solid #334155',
            }}
          >
            Browse curriculum (dashboard)
          </Link>
          <Link
            to="/domain/ai"
            style={{
              display: 'block',
              textAlign: 'center',
              background: '#6366f1',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: 10,
              padding: '14px 18px',
              fontWeight: 600,
            }}
          >
            Open AI domain
          </Link>
        </div>
      </main>

      <ChatWidget
        domain="ai"
        defaultOpen
        title="Your AI session coach"
        lessonContext={lessonContext}
        quickActions={QUICK_ACTIONS}
        onLaunch={handleLaunch}
        panelWidth={420}
      />
    </div>
  );
}
